/**
 * Italian (`it`) string catalogue — mirrors the key shape of `./en.ts`.
 * Brand names (`OVERVOLT`, `Sniper Rail`, `Arc Coil`, `Cryo Emitter`,
 * `Splash Mortar`, `Pulse Turret`, `Pylon`, `Flux`) stay in English; the
 * rest translates idiomatically into terse, gamey Italian.
 */

import type { Catalogue } from './en';

export const it: Catalogue = {
  meta: {
    name: 'Italiano',
    code: 'IT',
  },

  towers: {
    pulse: {
      label: 'Pulse Turret',
      short: 'PULSE',
      role: 'Generalista neutrale. Danno affidabile ovunque sul percorso.',
    },
    arc: {
      label: 'Arc Coil',
      short: 'ARC',
      role: 'Accoppiato alla rete. Più forte con surplus di Pylon, più debole in blackout.',
    },
    cryo: {
      label: 'Cryo Emitter',
      short: 'CRYO',
      role: 'Pittore di strozzature. Raggio corto — rallenta e marca ogni nemico in un punto.',
    },
    mortar: {
      label: 'Splash Mortar',
      short: 'MORT.',
      role: 'Fuoco indiretto. Posizionalo INDIETRO dal percorso — da vicino ha zona morta.',
    },
    sniper: {
      label: 'Sniper Rail',
      short: 'SNIPER',
      role: 'Perforazione in linea di tiro. Forte su lunghi rettilinei; debole negli angoli.',
    },
    pylon: {
      label: 'Pylon',
      short: 'PYLON',
      role: 'Alimentazione. Costruiscine abbastanza per coprire la richiesta (e un surplus per Arc).',
    },
    wall: {
      label: 'Wall Node',
      short: 'MURO',
      role: 'Blocco economico — solo in modalità Maze. Modella il tragitto dei nemici.',
    },
  },

  roleHints: {
    pulse: 'Pulse Turret: la tua scelta affidabile — danno solido ovunque sul percorso.',
    arc: 'Arc Coil: scala con il surplus di energia. Costruisci più Pylon per una catena più forte.',
    cryo: 'Cryo Emitter: pittura precisa della strozzatura. Rallenta e marca le poche caselle al pinch.',
    mortar: 'Splash Mortar: fuoco indiretto. Posizionalo INDIETRO dal percorso — da vicino non colpisce.',
    sniper: 'Sniper Rail: perforazione in linea di tiro. Allineato a un rettilineo lungo dà il massimo.',
    pylon: 'Power Pylon: nutre la rete. Costruisci abbastanza per coprire la richiesta delle torri.',
    wall: 'Muro: blocco economico — modella il tragitto che i nemici devono fare.',
  },

  hints: {
    build:
      'Costruisci una torre: scegline una sotto (o premi 1), poi clicca una casella illuminata vicino al percorso.',
    placed: 'Bene. Aggiungine un’altra, poi premi Spazio per lanciare l’ondata.',
    wave1: 'Le torri sparano da sole. Tieni la barra dell’energia fuori dal rosso.',
    select: 'Clicca una torre piazzata per selezionarla — U per migliorare, S per vendere.',
    brownout: 'Blackout! Torri rallentate. Costruisci un Pylon (3) per più energia.',
  },

  contextHints: {
    overload:
      'Overload: alza la prossima ondata per più ricompense — con più rischio. Avvio lento? Pompalo per Flux più rapido.',
    profileKey: 'Premi P per aprire il Profilo — il Flux sblocca torri, abilità e modalità.',
    endlessMod: 'Endless ruota i MODIFICATORI di ondata — adatta la composizione.',
    puzzleHover:
      'Modalità Puzzle: livelli logici fatti a mano con muri che bloccano la linea di vista. Un gioco completamente diverso.',
  },

  hud: {
    buildZone: 'COSTRUISCI',
    waveZone: 'ONDATA',
    abilitiesZone: 'ABILITÀ',
    overloadZone: 'OVERLOAD',

    startWave: 'LANCIA ONDATA',
    nextWave: 'PROSS. ONDATA',
    pause: 'PAUSA',
    resume: 'RIPRENDI',
    restart: 'RICOM.',
    deployHint: 'lancia [SPACE]',
    holdHint: 'pausa [SPACE]',
    rebootHint: 'reset [R]',
    nextWaveHint: 'PROSS. ONDATA',

    audioOn: 'AUDIO ON',
    audioOff: 'AUDIO OFF',

    credits: 'CREDITI',
    core: 'NUCLEO',
    wave: 'ONDATA',
    endless: 'ENDLESS',
    score: 'PUNTI',
    powerGrid: 'RETE',

    runMode: 'MODALITÀ',
    difficulty: 'DIFFICOLTÀ',
    profile: 'PROFILO',
    map: 'MAPPA',
    clickHint: '▷ clic',

    locked: 'BLOCCATO',
    lockedShort: 'BLOCC.',
    lockedTooltip:
      'BLOCCATO — sblocca per ✦{cost} Flux nel Profilo (premi P).',
    lockedTowerNotice: '{tower} bloccato',
    lockedAbility: 'Abilità bloccata',
    lockedOverload: 'Livello Overload bloccato',
    lockedMode: 'Modalità {mode} bloccata',
    lockedDifficulty: 'Hard bloccato',

    cheatPip: '🔓 CHEAT',
    profilePip: '👤 {name}',

    overloadIdle: '— overload attivo dall’ondata 1 —',
    overloadTier: 'TIER {tier} · {name}',
    overloadActive: '◉ ATTIVO {sec}s',

    maxLevel: 'LIVELLO MAX',
    upgrade: 'MIGLIORA L{level}',
    sell: 'VENDI',
    target: 'BERSAGLIO: {mode}',
    cycleTarget: 'cambia · [T]',
    sellHint: '+{refund}c · [S]',
    upgradeHint: '{cost}c · [U]',
    overclock: 'OVERCLOCK',
    overclockOn: 'OVERCLOCK ✦ ON',

    // Sprint 69 (i18n-gaps-extract-s69) — extracted from render.ts / hud.ts.
    buildHintIntro: "FASE DI COSTRUZIONE  //  arma una torre [1·2·3·4·5], piazzala e LANCIA ONDATA [SPACE]",
    buildHintReinforce: "FASE DI COSTRUZIONE  //  rinforza la rete e LANCIA ONDATA {wave} [SPACE]",
    mazeRoute: "P E R C O R S O   M A Z E",
    tiles: "TESSERE",
    abilityActive: "◉ ATTIVO {sec}s",
    costSuffix: '{cost}c',
    overclockPip: 'OC ×{n}',
    powerReadout: '{demand} / {supply}W  ·  {pct}%',
    towerCostDraw: '{cost}c {power}w',
    pylonCostSupply: '{cost}c +{supply}w',
    wallCost: '{cost}c 0w',
    endlessModBadge: 'MOD · {label}',
    startWaveOverload: '⚡{n} {tierName}',
    waveOverloadPip: '⚡{n} {tierName}',
    overclockNoticeOn: "{tower} Overclock ON",
    overclockNoticeOff: "{tower} Overclock OFF",

    tierMult: {
      hp: "PV",
      sp: "VL",
      cn: "NM",
      gd: "OR",
      sc: "PT",
    },
    spawnShort: {
      bit: "Bit",
      packet: "Pkt",
      daemon: "Dmn",
      fork: "Fork",
      cipher: "Cph",
      patch: "Pch",
      buffer: "Buf",
      streak: "Stk",
      kernel: "KERNEL",
    },
  },

  modes: {
    campaign: 'CAMPAGNA',
    endless: 'ENDLESS',
    maze: 'MAZE',
    puzzle: 'PUZZLE',
  },
  modeSub: {
    campaign: '10 ondate · vincere',
    endless: '✦ {cost}',
    maze: '✦ {cost}',
    puzzle: 'scegli livello',
  },
  modeBlurb: {
    campaign: '10 ondate. Boss all’ondata 10. Vinci e ti diplomi.',
    endless: 'Stessa mappa. Un modificatore per ondata. Adattati o filtra.',
    maze: 'Campo aperto. I muri tracciano la rotta. Rallentali o erodi il loro bottino.',
    puzzle: '10 livelli fatti a mano. Trova l’unica disposizione vincente.',
  },
  modeCard: {
    campaign: {
      tagline: 'Impara la rete, abbatti il boss.',
      bullet1: '10 ondate calibrate + boss',
      bullet2: 'Percorso S fisso — costruisci, ondata, migliora',
      bullet3: 'Vincere sblocca Endless su questa mappa',
      length: '~8 min',
      skill: 'Allena l’ordine di costruzione',
    },
    endless: {
      tagline: 'Adattati o filtra. Senza fine.',
      bullet1: 'Gioca Campagna 1–10, poi scala all’infinito',
      bullet2: 'Nuovo modificatore ogni ondata',
      bullet3: 'La run finisce quando il Nucleo cade',
      length: '~30 min',
      skill: 'Allena i grandi pivot',
    },
    maze: {
      tagline: 'Tu costruisci il labirinto che percorrono.',
      bullet1: 'Campo aperto — nessun percorso disegnato',
      bullet2: 'Torri e muri economici tracciano la rotta',
      bullet3: 'Rotta più lunga = più tempo nel fuoco',
      length: '~12 min',
      skill: 'Allena il routing spaziale',
    },
    puzzle: {
      tagline: 'Una disposizione vince. Trovala.',
      bullet1: '10 livelli, una sola soluzione',
      bullet2: 'Budget e ondata fissi',
      bullet3: 'Muri LOS + limiti di torri',
      length: '~2 min ciascuno',
      skill: 'Allena la deduzione',
    },
  },
  difficulty: {
    easy: 'FACILE',
    normal: 'NORMALE',
    hard: 'DIFFICILE',
  },
  difficultySub: {
    easy: 'lieve',
    normal: 'bilanciato',
    hard: '✦ {cost}',
  },

  overlay: {
    paused: 'PAUSA',
    pauseHint: 'PREMI  SPAZIO  PER  RIPRENDERE',

    missionComplete: '// MISSIONE COMPLETATA',
    gridSecured: 'RETE MESSA IN SICUREZZA',
    wavesRepelled: 'Tutte le {total} ondate d’assalto respinte.',
    continueEndless: 'CONTINUA  →  ENDLESS',
    unlockEndless: 'sblocca Endless nel Profilo per continuare',
    restartCampaign: 'oppure premi  R  per ricominciare la campagna',

    systemFailure: '// GUASTO DI SISTEMA',
    coreBreached: 'NUCLEO VIOLATO',
    endlessEnded: 'Corsa Endless terminata all’ondata {wave}.',
    reactorOverrun: 'Reattore travolto all’ondata {wave} su {total}.',
    runItBack: 'PREMI  R  PER  RIPROVARE',

    finalScore: 'PUNTEGGIO FINALE',
    newHighScore: '★  NUOVO RECORD  ★',
    newEndlessBest: '★  NUOVO RECORD ENDLESS  ★',
    highScore: 'RECORD',
    endlessBest: 'RECORD ENDLESS',

    // Sprint 69 — top-banner countdown overlays drawn by render.ts.
    aftershock: "// SCOSSA — DEBITO RETE  {sec}s //",
    overcharge: "// OVERCHARGE — RETE POTENZIATA  {sec}s //",
    brownout: "// CALO RETE — TORRI RIDOTTE //",
  },

  profile: {
    title: 'PROFILO',
    subtitle: '// META-PROGRESSIONE',
    closeHint: 'premi  P  o  ESC  per chiudere',
    tabUnlocks: 'SBLOCCHI',
    tabScoreboard: 'CLASSIFICA',
    flux: 'FLUX',
    unlockTree: 'ALBERO DEGLI SBLOCCHI',
    unlockedCount: '{owned} / {total} SBLOCCATI',
    lifetime: 'TOTALE',

    branchArsenal: 'ARSENALE',
    branchModes: 'MODALITÀ',
    branchChallenge: 'SFIDA',
    branchCosmetic: 'ESTETICA',

    statRunsPlayed: 'PARTITE GIOCATE',
    statCampaignWins: 'VITTORIE CAMPAGNA',
    statBestScore: 'MIGLIOR PUNTEGGIO',
    statDeepestEndless: 'ENDLESS PIÙ PROFONDO',
    statTotalFlux: 'FLUX TOTALE',

    personalBests: 'RECORD PERSONALI',
    waveLabel: 'ONDATA {wave}',

    runHistory: 'CRONOLOGIA',
    noRuns: 'Nessuna corsa ancora — finiscine una per iniziare la scalata.',
    earlierRuns: '+ {n} corsa precedente',
    earlierRunsPlural: '+ {n} corse precedenti',
    colRun: 'CORSA',
    colMode: 'MODO · DIFFICOLTÀ',
    colResult: 'ESITO',
    colOverload: 'OVL',
    colScore: 'PUNTI',
    colDeltaBest: 'Δ MIGL.',
    colFlux: 'FLUX',
    starBest: '★ MIGL.',

    statusUnlocked: 'SBLOCCATO',
    statusOwned: 'POSSEDUTO',
    statusRequiresPrereq: 'Prima {prereq}',
    statusFluxShort: 'Mancano ✦{short} Flux',
    statusAffordable: 'Acquistabile — clicca per sbloccare',
    statusCost: '✦{cost} per sbloccare',
    buyAction: 'COMPRA  ▸',
    nodeReq: 'REQ: {prereq}',

    profileLauncher: 'PROFILO',
    profileKeyHint: '[ P ]',
    viewScoreboard: 'VEDI  CLASSIFICA',
    newPersonalBest: '★  NUOVO RECORD PERSONALE  ★',
    fromYourBest: 'DAL TUO MIGLIORE',

    grantTower: 'TORRE D’ARMA',
    grantAbility: 'ABILITÀ ATTIVA',
    grantMode: 'MODALITÀ',
    grantDifficulty: 'DIFFICOLTÀ',
    grantOverload: 'LIVELLO OVERLOAD {tier}',
    grantTheme: 'TEMA TAVOLA',

    // Sprint 69 — unlock-tree hover preview.
    nodePreview: {
      towerMortar: "Tiro indiretto. Mettila DIETRO il percorso — vicino zona morta; obici sui cluster, rompe armatura.",
      towerSniper: "Lancia perforante in linea. Mira una lunga retta — tutti in linea prendono; debole in angoli.",
      abilityAirstrike: "Esplosione unica su una tessera (45c). Pulisci un cluster o intacca un grosso.",
      abilityEmp: "Stun pan-campo (80c). Congela tutti i nemici un attimo se la linea cede.",
      modeEndless: "Gioca oltre l’ondata 10 — ondate procedurali fino alla caduta del Nucleo.",
      modeMaze: "Modalità costruzione campo aperto. Le torri bloccano la loro tessera, A*-pathing attorno.",
      difficultyHard: "Ondate più dure; moltiplicatori oro e punti più grandi.",
      overloadCritical: "Overload tier 3 — alza il calore per moltiplicatori più grossi alla prossima ondata.",
      overloadMeltdown: "Overload tier 4 — l’ondata pre-set più dura. Moltiplicatori top.",
      themeAmber: "Palette cosmetica — terminale CRT amber caldo. Puramente cosmetico.",
      themePhosphor: "Palette cosmetica — verde fosforo vintage. Puramente cosmetico.",
    },
  },

  help: {
    eyebrow: '/ /  R I F E R I M E N T O',
    title: 'MANUALE OPERATIVO',
    controls: 'COMANDI',
    powerGrid: 'LA RETE',
    powerGridBody:
      'Le armi consumano energia; i Pylon la forniscono. Quando la domanda supera l’offerta, la rete va in blackout e tutte le torri rallentano — costruisci Pylon per tenerle in fuoco.',
    objective: 'OBIETTIVO',
    objectiveBody:
      'Ripulisci tutte le 15 ondate della campagna per vincere; perdi quando le vite del Nucleo scendono a zero. La modalità Endless dura per sempre.',
    mazeMode: 'MODALITÀ MAZE',
    mazeModeBody:
      'Una terza modalità in campo aperto: le torri bloccano la loro casella e i nemici le aggirano con A*, quindi costruisci tu il labirinto. Non puoi murare il Nucleo del tutto.',
    glitches: 'GLITCH',
    towers: 'TORRI',
    abilities: 'ABILITÀ',
    replayIntro: '↺  RIVEDI INTRO',
    closeHint: 'Esc / H / ? o clic ovunque per chiudere',
  },

  intro: {
    eyebrow: '/ /  B R I E F I N G   D I   S I S T E M A',
    titleBig: 'OVERVOLT',
    subtitle: 'Difendi il Nucleo',
    line1:
      'Programmi corrotti avanzano nel circuito verso il tuo Nucleo. Costruisci torri per distruggerli prima che sfondino.',
    line2:
      '⚡ Ogni torre consuma energia. Se costruisci troppo, la rete va in blackout — tutte le torri rallentano insieme. Costruisci Pylon per mantenere la corrente.',
    line3: 'Costruisci nella fase di calma, poi premi Lancia Ondata. Sopravvivi a 15 ondate per vincere.',
    line4: 'Mouse costruisce · 1–5 selezionano · Spazio start/pausa · U migliora · S vendi',
    gotIt: 'CAPITO  →',
    gotItHint: 'premi un tasto qualsiasi',
  },

  puzzle: {
    label: 'PUZZLE',
    levels: {
      'p0-first-light': {
        name: 'Prima Luce',
        hint: 'Posiziona un Pulse accanto al corridoio. Qualsiasi casella a tiro va bene.',
      },
      'p1-long-lance': {
        name: 'Lancia Lunga',
        hint: 'Un Sniper allineato alla corsia perfora ogni nemico.',
      },
      'p2-over-the-wall': {
        name: 'Oltre il Muro',
        hint: 'Il muro blocca ogni raggio diretto. I colpi di mortaio passano a parabola.',
      },
      'p3-funhouse-mirror': {
        name: 'Specchio Deformante',
        hint: 'Un Sniper infilato nella fessura colpisce entrambe le corsie.',
      },
      'p4-surplus-riches': {
        name: 'Ricchezze in Eccesso',
        hint: 'Costruisci Pylon oltre la banda libera. L’Arc Coil prospera col surplus.',
      },
      'p5-pinch-point': {
        name: 'Strozzatura',
        hint: 'Il Cryo dipinge il pinch; i Pulse intorno chiudono il gruppo.',
      },
      'p6-cipher-gauntlet': {
        name: 'Guanto Cipher',
        hint: 'Il mortaio sbrana l’armatura; il Cryo dipinge la curva; lo Sniper perfora il tratto basso. Usa tutto il kit.',
      },
      'p7-kernel-echo': {
        name: 'Eco del Kernel',
        hint: 'I mortai lanciano oltre i muri di LOS. Lo Sniper infila dal basso. Tieni d’occhio il Power Drain.',
      },
      'p8-overclock-crunch': {
        name: 'Stretta Overclock',
        hint: 'Un solo Pulse non basta. Overclock nella finestra di burst — la cadenza costante non è sufficiente.',
      },
      'p9-pylon-pinch': {
        name: 'Stretta dei Pylon',
        hint: 'Raggruppa i Pylon tra le tue torri — il cap dell’aura premia un’area compatta.',
      },
    },
    briefing: {
      eyebrow: "BRIEFING PUZZLE",
      startBtn: "Avvia ▷",
      cancelBtn: "Annulla",
      sectionConstraints: "VINCOLI",
      sectionTowers: "TORRI",
      sectionWinCondition: "CONDIZIONE DI VITTORIA",
      budgetLabel: "Budget oro",
      wavesLabel: "Ondate",
      towerAllowed: "PERMESSA",
      towerForbidden: "BLOCCATA",
      resultEyebrowWon: "PUZZLE COMPLETATO",
      resultEyebrowLost: "PUZZLE FALLITO",
      resultTitleWon: "RISOLTO!",
      resultTitleLost: "RIPROVA",
      resultStatTowers: "TORRI",
      resultStatGold: "ORO SPESO",
      resultStatLives: "VITE RIMASTE",
      resultNextBtn: "Livello successivo ▷",
      resultReplayBtn: "Ripeti",
      resultResetBtn: "Resetta tentativo",
      resultBackBtn: "Torna alla selezione",
    },

    // Sprint 69 — puzzle level-select overlay chrome.
    select: {
      subtitle: "Livelli logici fatti a mano — deduci l’unica disposizione vincente.",
      close: "Chiudi",
      cardTitle: 'P{index}  {name}',
      lockedCard: "🔒 BLOCCATO",
      solved: "✓ Risolto",
      unsolved: "▷ Irrisolto",
      noSolve: "Ancora non risolto",
      bestLine: "Migliore: {towers}t · {gold}g · {power}p",
      allowed: "Permesso: {list}",
      parPlaceholder: "par: — / — / —",
      parRow: "{towers}t · {gold}g · {power}p",
    },
  },

  localeChip:
 {
    label: 'LINGUA',
    en: 'EN',
    cs: 'CS',
  },

  resume: {
    title: 'Riprendere la corsa precedente?',
    resume: 'Riprendi',
    newRun: 'Nuova',
    hint: '[Enter] Riprendi   ·   [Esc] Nuova',
    detail: "{mode} · {difficulty} · ondata {wave} · {towers} torri · {gold}g · {lives}♥",
  },

  // -------- Sprint 69: Kernel boss UI chrome ---------------------------------
  boss: {
    kernelHeader: "⚠  IL KERNEL",
    phase: "FASE {phase} · {name}",
    powerDrain: "⚡ DRENAGGIO ENERGIA",
    kernelIncoming: "⚠  KERNEL  IN ARRIVO  ⚠",
    phaseName: {
      boot: "BOOT SEQUENCE",
      fork: "FORK BOMB",
      panic: "KERNEL PANIC",
    },
  },

  // -------- Sprint 69: Map registry display strings --------------------------
  maps: {
    default: { label: "Datawash", tagline: "Curva a S aperta — il classico OVERVOLT." },
    cramped: { label: "Switchyard", tagline: "Corsie strette — ogni tessera è contesa." },
    lines: { label: "Long Lines", tagline: "Lunghe linee dritte — guida la lancia." },
    serpentine: { label: "Snake Run", tagline: "Serpentina stretta — il Cryo painter brilla." },
  },

  // -------- Sprint 69: Ability display names ---------------------------------
  abilities: {
    overcharge: { label: 'Overcharge' },
    airstrike: { label: 'Airstrike' },
    empBurst: { label: 'EMP Burst' },
  },

  // -------- Sprint 69: Overload tier names -----------------------------------
  overload: {
    tierName: {
      nominal: "Nominale",
      charged: "Carico",
      surging: "Crescente",
      critical: "Critico",
      meltdown: "Meltdown",
    },
  },

  // -------- Sprint 69: Endless modifier labels -------------------------------
  endlessMod: {
    armoredSurge: { label: "Carica Corazzata" },
    fastPack: { label: "Branco Veloce" },
    scatteredSpread: { label: "Dispersione" },
    streakRunners: { label: "Corridori Streak" },
    daemonRush: { label: "Carica Daemon" },
  },

  // -------- Sprint 69: Enemy display names (Help roster) ---------------------
  enemies: {
    bit: { label: "Bit" },
    packet: { label: "Packet" },
    daemon: { label: "Daemon" },
    fork: { label: "Fork" },
    cipher: { label: "Cipher" },
    patch: { label: "Patch" },
    buffer: { label: "Buffer" },
    streak: { label: "Streak" },
    kernel: { label: "Kernel" },
  },

  // -------- Sprint 69: Help-panel enemy one-liners ---------------------------
  enemyRole: {
    bit: "Carne da cannone — economico, lento, fragile.",
    packet: "Veloce e debole; corre al Nucleo.",
    daemon: "Spugna HP; colpisce duro a un leak.",
    fork: "Si divide in due Bits alla morte.",
    cipher: "Corazzato — strappalo con Corrosione Mortaio.",
    patch: "Cura i Glitch vicini; abbattilo per primo.",
    buffer: "Scudo rigenerante; spezzalo in burst.",
    streak: "Sprinta da solo; resiste allo slow. Copri il percorso.",
    kernel: "Boss a fasi — drena la tua energia. Burstalo prima della fase successiva.",
  },

  // -------- Sprint 69: Help-panel ability one-liners -------------------------
  abilityRole: {
    overcharge: "Spinge la rete: ogni torre tira più veloce per un attimo. Pianificalo su un’ondata pesante.",
    airstrike: "Esplosione unica su una tessera bersaglio. Pulisci un cluster o intacca un grosso.",
    empBurst: "Stun pan-campo — tutti i nemici si congelano un attimo. Compra tempo se la linea cede.",
  },

  // -------- Sprint 69: Tooltip chrome ----------------------------------------
  tooltip: {
    levelOf: "LV {level}/{max}",
    credits: "{cost} crediti",
    next: "NEXT  →  LV {level}   {cost} crediti",
    cooldown: "COOLDOWN  {sec}s",
    stat: {
      supply: "ALIMENTAZIONE  +{n}w alla rete",
      dmgRngCd: "DMG {damage}    RNG {range}    CD {cd}s",
      pwrChain: "PWR {power}w    CATENA {chain}",
      pwrAoe: "PWR {power}w    AOE {targets} bersagli",
      slow: "SLOW ×{factor}  per  {sec}s",
      brittle: "FRAGILE +{pct}% dmg dalle altre torri",
      pwrBlast: "PWR {power}w    BLAST {tiles} tessere",
      pwrSingle: "PWR {power}w    1 bersaglio",
    },
  },

  // -------- Sprint 69: Help-panel control-row labels -------------------------
  controls: {
    arm: "Arma Pulse / Arc / Pylon / Cryo / Mortaio / Sniper",
    mouse: "Piazza su tessera · clicca una torre per selezionare",
    space: "Lancia ondata · metti in pausa",
    fast: "Avanti veloce (1× ⇄ 2×)",
    upgradeSell: "Migliora / vendi la torre selezionata",
    target: "Cambia modalità bersaglio della torre selezionata",
    profile: "Apri Profilo (albero · classifica)",
    utility: "Annulla · muto · riavvia corsa",
  },

  // -------- Sprint 69: Unlock-tree node display names ------------------------
  unlocks: {
    towerMortar: { name: "Splash Mortar" },
    towerSniper: { name: "Sniper Rail" },
    abilityAirstrike: { name: "Airstrike" },
    abilityEmp: { name: "EMP Burst" },
    modeEndless: { name: "Modalità Endless" },
    modeMaze: { name: "Modalità Maze" },
    difficultyHard: { name: "Difficoltà Hard" },
    overloadCritical: { name: "Overload: Critico" },
    overloadMeltdown: { name: "Overload: Meltdown" },
    themeAmber: { name: "Terminale Amber" },
    themePhosphor: { name: "Verde Fosforo" },
  },

  // Sprint 73 — tutorial interattivo (docs/TUTORIAL_DESIGN.md §6).
  tutorial: {
    button: {
      start: 'AVVIA TUTORIAL',
      startSub: 'Prima volta? Fai il tour →',
      replay: 'RIPETI TOUR',
      replaySub: 'Rinfresca le basi',
    },
    step: {
      welcome: {
        title: 'Benvenuto in OVERVOLT',
        body: "Sei l'IA difensiva di un reattore in avaria. Programmi corrotti marciano sul circuito verso il tuo Core — distruggili prima che sfondino.",
      },
      path: {
        title: 'Il percorso',
        body: 'I nemici seguono sempre questa curva a S, dalla casella di spawn a sinistra al Core a destra. Decidi tu dove piantare le torrette.',
      },
      firstPlace: {
        title: 'Piazza la prima torre',
        body: 'Una Pulse Turret costa 50 crediti e assorbe 4 di energia. Clicca la casella illuminata accanto al percorso.',
      },
      wave1: {
        title: 'Lancia la prima ondata',
        body: 'Premi AVVIA ONDATA. La Pulse spara automaticamente a tutto ciò che è a portata.',
      },
      towerRoles: {
        title: 'Cinque torri, cinque ruoli',
        body: 'Pulse: affidabile ovunque. Sniper: rettilinei lunghi. Mortar: fuoco indiretto, INDIETRO dal percorso. Arc: scala col surplus. Cryo: dipinge un collo di bottiglia.',
      },
      powerGrid: {
        title: 'La rete elettrica',
        body: 'Ogni torre consuma. Quando la domanda supera l’offerta, la rete cade in brownout e tutte rallentano. Costruisci Pylon — i vicini alzano il rateo di fuoco.',
      },
      overloadDial: {
        title: 'Overload — rischio vs ricompensa',
        body: 'Alza la manopola per la prossima ondata: nemici più tosti, più Flux. Abbassala per giocare sicuro.',
      },
      overclock: {
        title: 'Overclock di una torre',
        body: 'Attiva OVERCLOCK su una torre selezionata per una finestra burst: fuoco più rapido, più consumo. Vincita burst, perdita a regime.',
      },
      abilities: {
        title: 'Abilità attive',
        body: 'Tre slot: Overcharge potenzia tutta la rete. Airstrike lascia una zona ustionata durevole. EMP Burst stordisce tutti. Pianifica — non premere in panico.',
      },
      mazePreview: {
        title: 'Modalità Maze',
        body: 'Nessun percorso fisso — le torri bloccano le caselle e i nemici A*-routano attorno. Costruisci tu il tragitto che devono percorrere.',
      },
      puzzlePreview: {
        title: 'Modalità Puzzle',
        body: 'Livelli logici fatti a mano con muri a linea di vista. Budget fisso, ondata fissa — trova l’unica disposizione vincente. Un altro gioco.',
      },
      wrapUp: {
        title: 'Sei pronto',
        body: 'Le partite danno FLUX — spendilo nel Profilo per sbloccare torri, abilità e modalità. Inizia con Campaign; torna per Endless, Maze e Puzzle.',
      },
    },
    ui: {
      eyebrow: 'PASSO {n} / {total}',
      next: 'AVANTI →',
      back: '← INDIETRO',
      skip: 'SALTA TOUR',
      play: 'GIOCA  ▸',
      skipConfirm: 'Saltare il tour? Puoi riprenderlo in ogni momento dalla schermata titolo.',
    },
  },
};
